// src/components/common/NavbarMobileMenu.tsx
import React from 'react';
import { Box, Typography, Avatar, Button, Divider } from '@mui/material';
import { Dashboard, Logout } from '@mui/icons-material';
import { UserRole } from '../../types';

interface NavbarMobileMenuProps {
  user: { username: string; email?: string; role: string };
  isRTL: boolean;
  t: (key: string) => string;
  onDashboard: () => void;
  onLogout: () => void;
  onClose: () => void;
}

const NavbarMobileMenu: React.FC<NavbarMobileMenuProps> = ({
  user,
  isRTL,
  t,
  onDashboard,
  onLogout,
  onClose,
}) => {
  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'none' },
        borderTop: '1px solid rgba(209, 213, 219, 0.3)',
        py: 2,
        bgcolor: 'white',
      }}
    >
      {/* User Info */}
      <Box
        sx={{
          px: 2,
          py: 2,
          mb: 2,
          bgcolor: 'rgba(101, 35, 209, 0.05)',
          borderRadius: 2,
          mx: 2,
          border: '1px solid rgba(101, 35, 209, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#6523d1',
              fontFamily: 'Cairo, Arabic, sans-serif',
            }}
          >
            {user.username.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ textAlign: 'right', flex: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: 'rgb(17, 24, 39)',
                fontWeight: 600,
                fontFamily: 'Cairo, Arabic, sans-serif',
              }}
            >
              {user.username}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgb(107, 114, 128)',
                fontSize: '0.75rem',
                fontFamily: 'Cairo, Arabic, sans-serif',
              }}
            >
              {user.email || user.username}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: '#6523d1',
            fontSize: '0.75rem',
            fontWeight: 500,
            fontFamily: 'Cairo, Arabic, sans-serif',
            display: 'block',
            textAlign: 'right',
          }}
        >
          {user.role === UserRole.ADMIN ? t('nav.systemAdmin') : t('nav.restaurantOwner')}
        </Typography>
      </Box>

      {/* Menu Items */}
      <Box sx={{ px: 2 }}>
        <Button
          fullWidth
          onClick={() => {
            onDashboard();
            onClose();
          }}
          sx={{
            color: 'rgb(17, 24, 39)',
            fontFamily: 'Cairo, Arabic, sans-serif',
            textAlign: 'right',
            justifyContent: 'flex-start',
            textTransform: 'none',
            py: 1.5,
            px: 2,
            mb: 1,
            borderRadius: 2,
            transition: 'all 0.3s ease-out',
            '&:hover': { bgcolor: 'rgba(101, 35, 209, 0.05)' },
          }}
        >
          <Dashboard sx={{ [isRTL ? 'ml' : 'mr']: 2, fontSize: 20, color: '#6523d1' }} />
          {t('nav.dashboard')}
        </Button>

        <Divider sx={{ borderColor: 'rgba(209, 213, 219, 0.3)', my: 1 }} />

        <Button
          fullWidth
          onClick={() => {
            onLogout();
            onClose();
          }}
          sx={{
            color: 'rgb(239, 68, 68)',
            fontFamily: 'Cairo, Arabic, sans-serif',
            textAlign: 'right',
            justifyContent: 'flex-start',
            textTransform: 'none',
            py: 1.5,
            px: 2,
            borderRadius: 2,
            transition: 'all 0.3s ease-out',
            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.05)' },
          }}
        >
          <Logout sx={{ [isRTL ? 'ml' : 'mr']: 2, fontSize: 20 }} />
          {t('nav.logout')}
        </Button>
      </Box>
    </Box>
  );
};

export default NavbarMobileMenu;
